/**
 * Utilities for calibration series tail computation.
 * Ported from compute/py_modules/continuous_calibration/operation/steps/calibration_series.py
 */

/**
 * @param {{ valid_at: number, level_in_grams: number, purpose?: string }[]} binSetLevels
 * @returns {{ occurredAt: number, value: number }[]}
 */
export function buildResetOverrides(binSetLevels = []) {
  const resets = binSetLevels
    .filter((level) => level.purpose === 'reset')
    .map((level) => ({
      occurredAt: level.valid_at,
      value: level.level_in_grams,
    }))
    .sort((a, b) => a.occurredAt - b.occurredAt);

  if (resets.length) {
    return resets;
  }

  const calibrates = binSetLevels
    .filter((level) => level.purpose === 'calibrate')
    .sort((a, b) => a.valid_at - b.valid_at);

  if (!calibrates.length) {
    return [];
  }

  return [
    {
      occurredAt: calibrates[0].valid_at,
      value: calibrates[0].level_in_grams,
    },
  ];
}

/**
 * Prepend a prior-series anchor and drop overrides before it.
 * @param {{ occurredAt: number, value: number }[]} overrides
 * @param {{ occurredAt: number, value: number } | null} initialAnchor
 * @returns {{ occurredAt: number, value: number }[]}
 */
export function applyInitialAnchor(overrides, initialAnchor) {
  if (!initialAnchor || !overrides?.length) {
    return overrides ?? [];
  }

  const anchorAt = initialAnchor.occurredAt;
  const anchorValue = initialAnchor.value;
  const filtered = overrides.filter((o) => o.occurredAt >= anchorAt);

  if (filtered.length && filtered[0].occurredAt === anchorAt) {
    return [{ occurredAt: anchorAt, value: anchorValue }, ...filtered.slice(1)];
  }
  return [{ occurredAt: anchorAt, value: anchorValue }, ...filtered];
}

/**
 * @param {{ feed_frame_summaries_v2?: { end_timestamp: number, total_consumption: number }[] }[]} feedLines
 * @param {number} afterTs
 * @returns {{ occurredAt: number, value: number }[]}
 */
export function buildFeedEventsFromHourlySummaries(feedLines = [], afterTs) {
  const events = [];

  feedLines.forEach((line) => {
    line.feed_frame_summaries_v2?.forEach((summary) => {
      if (summary.end_timestamp > afterTs) {
        events.push({
          occurredAt: summary.end_timestamp,
          value: summary.total_consumption ?? 0,
        });
      }
    });
  });

  return events.sort((a, b) => a.occurredAt - b.occurredAt);
}

/**
 * @param {{ rawOccurredAt: number, value: number }[]} binLevelTimeSeries
 * @returns {{ t: number, v: number }[]}
 */
export function toVisxSeries(binLevelTimeSeries) {
  const points = [];
  const seen = new Set();

  for (const point of binLevelTimeSeries) {
    const t = point.rawOccurredAt;
    const v = point.value;
    const key = `${t}:${v}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    points.push({ t, v });
  }

  return points.sort((a, b) => a.t - b.t);
}

/**
 * Ensure encoded tail includes the exact cached anchor for chart stitching.
 * @param {{ t: number, v: number }[]} points
 * @param {{ t: number, v: number }} anchor
 * @returns {{ t: number, v: number }[]}
 */
export function bridgeTailToAnchor(points, anchor) {
  if (!anchor) {
    return points;
  }

  const hasAnchor = points.some((p) => p.t === anchor.t && p.v === anchor.v);
  if (hasAnchor) {
    return points;
  }

  const afterAnchor = points.filter((p) => p.t > anchor.t);
  if (!afterAnchor.length) {
    return [{ t: anchor.t, v: anchor.v }];
  }

  return [{ t: anchor.t, v: anchor.v }, ...afterAnchor];
}
